/**
 * Who is using this browser?
 *
 * A family machine has more than one person on it, and until now the app had
 * no way to say so. A parent signed out and the next parent signed in with a
 * password; a child used `/me` and the two surfaces never acknowledged each
 * other. This is the one screen that does.
 *
 * WHAT IT SHOWS, and what each one costs to get into:
 *
 *   the account signed in now   nothing, it is already open
 *   a parent with a PIN         six digits
 *   a parent without one        their Cognito password, as before
 *   the children                nothing here -- it hands off to `/me`, where
 *                               each name is opened with that child's own key
 *
 * THE CHILDREN ARE A COUNT AND A DOOR, not names. Putting names here would
 * mean this screen fetching each child's state with their own device token,
 * which is the child app's job and its storage; `/me` already has a "Who is
 * this?" picker that does it properly. So the handoff is the design rather
 * than a shortcut -- see `childSessionCount`.
 *
 * NOTHING HERE IS A SECURITY BOUNDARY. The list is read out of localStorage
 * and anybody at this keyboard can read the same thing. What stops the person
 * reading it from getting IN is the PIN, the password and the child's own
 * key -- none of which is on this screen.
 */

import { BackButton } from '../components/BackButton';
import { useState } from 'react';
import { MAX_ATTEMPTS, lockedAccounts, type LockedAccount } from '../auth/pin';
import { signInWithPin } from '../auth/cognito';
import { childSessionCount } from '../child/session';
import { PinEntry } from '../components/PinEntry';

export function AccountPicker({
  onBack,
  resumeSub,
  onResumed,
  onUseAnother,
  onCreateAccount,
}: {
  /** Back to the front page. Without it the picker was a dead end. */
  onBack?: () => void;
  /**
   * The account that was open when Switch was pressed. Back then resumes it --
   * its PIN, then its dashboard -- rather than leaving for the front page.
   */
  resumeSub?: string;
  onResumed: () => void;
  onUseAnother: () => void;
  onCreateAccount: () => void;
}) {
  const [accounts, setAccounts] = useState<LockedAccount[]>(() => lockedAccounts());
  const [children] = useState(() => childSessionCount());
  const [chosen, setChosen] = useState<LockedAccount | null>(null);

  if (chosen) {
    return (
      <PinPrompt
        account={chosen}
        onOpened={onResumed}
        // NOT `forgetPin` first. Destroying the record here would let anybody
        // who can reach this screen wipe a parent's PIN with one click, and
        // forgetting it today does not mean they have forgotten it for good.
        // Signing in and setting a PIN again replaces the record anyway.
        onForgot={onUseAnother}
        onGone={() => {
          // The record is destroyed -- five wrong PINs, or a token Cognito
          // would not take. Back to the list, minus this account, with the
          // explanation on the sign-in button rather than a dead end.
          setAccounts(lockedAccounts());
          setChosen(null);
        }}
        onBack={() => setChosen(null)}
      />
    );
  }

  // No "signed in now" row, and there cannot be one. Reaching this screen
  // always ends the session that was open -- whether by going idle or by a
  // parent pressing Switch -- so every account here is locked by definition.
  const nothingSaved = accounts.length === 0 && children === 0;

  return (
    <>
      {onBack ? (
        <BackButton
          label="Back"
          className="picker-back"
          onClick={() => {
            const mine = resumeSub ? accounts.find((account) => account.sub === resumeSub) : undefined;
            if (mine) setChosen(mine);
            else onBack();
          }}
        />
      ) : null}
      <h2>Who&rsquo;s using Birbly?</h2>
      {nothingSaved ? (
        <p>Sign in to pick up where you left off.</p>
      ) : (
        <p>This browser is set up for more than one person.</p>
      )}

      <ul className="accounts">
        {accounts.map((account) => (
          <li key={account.sub}>
            <button className="account-row" onClick={() => setChosen(account)}>
              <span className="account-who">{account.email || 'A parent'}</span>
              <span className="account-how">Locked &mdash; enter PIN</span>
            </button>
          </li>
        ))}

        {children > 0 ? (
          <li>
            <button
              className="account-row"
              onClick={() => window.location.assign('/me')}
            >
              <span className="account-who">
                {children === 1 ? 'A child' : `${children} children`}
              </span>
              <span className="account-how">
                {children === 1 ? 'Their own page' : 'Their own pages'}
              </span>
            </button>
          </li>
        ) : null}
      </ul>

      <div className="actions stacked">
        <button className="text-button" onClick={onUseAnother}>
          Use a different account
        </button>
        <button className="text-button" onClick={onCreateAccount}>
          Create an account
        </button>
      </div>
    </>
  );
}

/**
 * The PIN prompt for one locked account.
 *
 * Counts down out loud after the first wrong answer. Silence would be kinder
 * in the moment and much worse at the fifth: a parent whose record has just
 * been destroyed should have been able to see it coming, because the way back
 * is a full sign-in and that is worth a warning.
 */
function PinPrompt({
  account,
  onOpened,
  onGone,
  onForgot,
  onBack,
}: {
  account: LockedAccount;
  onOpened: () => void;
  onGone: () => void;
  /** Sign in with a password instead. The locked record is left alone. */
  onForgot: () => void;
  onBack: () => void;
}) {
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [left, setLeft] = useState<number | null>(null);
  const [error, setError] = useState('');

  // Six digits is enough to know they have finished typing, so the button is
  // a fallback rather than the way in.
  async function attempt(entered: string): Promise<void> {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const outcome = await signInWithPin(account.sub, entered);
      if (outcome.status === 'ok') {
        onOpened();
        return;
      }
      if (outcome.status === 'gone') {
        onGone();
        return;
      }
      setLeft(outcome.left);
      setPin('');
      setError(
        outcome.left === 1
          ? 'That is not the PIN. One more try before this account has to sign in again.'
          : `That is not the PIN. ${outcome.left} tries left.`,
      );
    } catch {
      setError('That did not work. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void attempt(pin);
      }}
    >
      <h2>{account.email || 'Welcome back'}</h2>
      <p>Enter this account&rsquo;s PIN to carry on.</p>

      <PinEntry
        label="PIN"
        value={pin}
        onChange={setPin}
        onComplete={(entered) => void attempt(entered)}
        autoFocus
        disabled={busy}
      />

      {error ? <p className="error">{error}</p> : null}
      {left === null ? (
        <p className="footnote">
          Six digits. After {MAX_ATTEMPTS} wrong tries this account signs in
          with its password again.
        </p>
      ) : null}

      <div className="actions">
        <button aria-busy={busy} className="primary" type="submit" disabled={busy || pin.length === 0}>
          {busy ? 'Unlocking…' : 'Unlock'}
        </button>
        <button type="button" disabled={busy} onClick={onBack}>
          Back
        </button>
      </div>

      {/* A real way out, not an instruction to go and find one. A parent who
          cannot remember six digits is not going to enjoy being told to press
          Back and read a list. */}
      <button type="button" className="text-button" disabled={busy} onClick={onForgot}>
        Forgot your PIN?
      </button>
      <p className="footnote">
        That signs you in with your password instead. Your PIN is not stored
        anywhere we can read, so it cannot be looked up &mdash; but you can set
        the same six digits again afterwards.
      </p>
    </form>
  );
}
